import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ddtApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { ArrowLeft, RotateCcw, Trash2, Trash } from 'lucide-react';
import { toast } from 'sonner';

const TrashPage = () => {
  const { user } = useAuth();
  const isSlave = user?.role === 'company_user';
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDialog, setConfirmDialog] = useState(null); // { action: 'restore'|'purge', ddt }
  const [purgePwd, setPurgePwd] = useState('');

  useEffect(() => { fetchList(); }, []);

  const fetchList = async () => {
    try {
      const r = await ddtApi.trashList();
      setList(r.data);
    } catch (e) { toast.error('Errore caricamento cestino'); }
    finally { setLoading(false); }
  };

  const doAction = async () => {
    if (!confirmDialog) return;
    const { action, ddt } = confirmDialog;
    if (action === 'purge' && (purgePwd || '').trim() !== '1997') {
      toast.error('Codice di sicurezza errato — il DDT NON è stato eliminato definitivamente');
      return;
    }
    try {
      if (action === 'restore') {
        await ddtApi.restore(ddt.id);
        toast.success(`DDT ${ddt.number}-${ddt.year} ripristinato`);
      } else {
        await ddtApi.purge(ddt.id, '1997');
        toast.success(`DDT ${ddt.number}-${ddt.year} eliminato definitivamente`);
      }
      setConfirmDialog(null);
      setPurgePwd('');
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    }
  };

  const fmtCur = (v) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(v || 0);
  const fmtDateTime = (d) => d ? new Date(d).toLocaleString('it-IT') : '-';
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '-';

  return (
    <div data-testid="trash-page" className="space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/ddt">
          <Button variant="ghost" size="icon" data-testid="trash-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
        </Link>
        <div>
          <h1 className="text-2xl font-bold tracking-tight font-['Manrope']">Cestino DDT</h1>
          <p className="text-muted-foreground text-sm">
            DDT eliminati. Puoi ripristinarli o eliminarli definitivamente.
          </p>
        </div>
      </div>

      <Card className="card-swiss">
        <CardContent className="p-0">
          {loading ? <div className="p-8 text-center text-muted-foreground">Caricamento...</div> :
           list.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Trash className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>Il cestino è vuoto</p>
              <p className="text-xs mt-1">I DDT eliminati appariranno qui</p>
            </div>
           ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="table-header-swiss w-[100px]">N° DDT</TableHead>
                  <TableHead className="table-header-swiss">Cliente</TableHead>
                  <TableHead className="table-header-swiss w-[100px]">Data DDT</TableHead>
                  <TableHead className="table-header-swiss w-[120px] text-right">Totale</TableHead>
                  <TableHead className="table-header-swiss w-[170px]">Eliminato il</TableHead>
                  <TableHead className="table-header-swiss w-[120px]">Eliminato da</TableHead>
                  <TableHead className="table-header-swiss w-[170px]">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.map((ddt) => (
                  <TableRow key={ddt.id} className="table-row-swiss bg-red-50/30">
                    <TableCell className="table-cell-swiss font-['JetBrains_Mono'] font-medium">{ddt.number}-{ddt.year}</TableCell>
                    <TableCell className="table-cell-swiss">{ddt.client_name || <span className="text-muted-foreground italic">—</span>}</TableCell>
                    <TableCell className="table-cell-swiss">{fmtDate(ddt.date)}</TableCell>
                    <TableCell className="table-cell-swiss text-right font-['JetBrains_Mono']">{fmtCur(ddt.total_amount)}</TableCell>
                    <TableCell className="table-cell-swiss text-xs text-muted-foreground">{fmtDateTime(ddt.deleted_at)}</TableCell>
                    <TableCell className="table-cell-swiss text-xs font-['JetBrains_Mono']">{ddt.deleted_by || '—'}</TableCell>
                    <TableCell className="table-cell-swiss">
                      <div className="flex gap-1">
                        {!isSlave && (
                          <>
                            <Button
                              data-testid={`restore-${ddt.id}`}
                              variant="outline"
                              size="sm"
                              onClick={() => setConfirmDialog({ action: 'restore', ddt })}
                              className="h-8 text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                              title="Ripristina"
                            >
                              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Ripristina
                            </Button>
                            <Button
                              data-testid={`purge-${ddt.id}`}
                              variant="ghost"
                              size="icon"
                              onClick={() => setConfirmDialog({ action: 'purge', ddt })}
                              className="h-8 w-8 text-destructive"
                              title="Elimina definitivamente"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        {isSlave && <span className="text-[10px] text-muted-foreground italic">Solo titolare</span>}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
           )}
        </CardContent>
      </Card>

      <AlertDialog open={!!confirmDialog} onOpenChange={(o) => { if (!o) { setConfirmDialog(null); setPurgePwd(''); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmDialog?.action === 'restore' ? 'Ripristina DDT' : 'Elimina definitivamente?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDialog?.action === 'restore'
                ? `Il DDT ${confirmDialog?.ddt?.number}-${confirmDialog?.ddt?.year} tornerà visibile nella lista DDT.`
                : `Il DDT ${confirmDialog?.ddt?.number}-${confirmDialog?.ddt?.year} verrà eliminato in modo permanente. Operazione NON reversibile.`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          {confirmDialog?.action === 'purge' && (
            <div className="py-2">
              <label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Codice di sicurezza</label>
              <input
                data-testid="purge-pwd-input"
                type="password"
                autoFocus
                value={purgePwd}
                onChange={(e) => setPurgePwd(e.target.value)}
                placeholder="Inserisci codice"
                className="w-full h-10 px-3 border border-input rounded-md font-['JetBrains_Mono'] text-center text-lg tracking-widest"
                maxLength={10}
              />
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-trash-action-btn"
              onClick={doAction}
              disabled={confirmDialog?.action === 'purge' && !purgePwd}
              className={confirmDialog?.action === 'restore' ? 'bg-emerald-700 hover:bg-emerald-800' : 'bg-destructive hover:bg-destructive/90 disabled:opacity-50'}
            >
              {confirmDialog?.action === 'restore' ? 'Ripristina' : 'Elimina per sempre'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TrashPage;
